import React from "react";
import { Box } from "rebass/styled-components";

import "../../../../corewellhealth/src/styles/typography.scss";
import { layout } from "../../../constants/constants";
import { DeviceType } from "../../hooks/use-breakpoint/device-type";
import useBreakpoint from "../../hooks/use-breakpoint/useBreakpoint";
import {
  AccordionBlock,
  DynamicCardContainerContent,
  DynamicContentContainerConfig,
  FeatureToggle,
  IconPack
} from "../../models/app-data-model";
import { sanitizeRichText } from "../../services/string-manipulation/string-manipulation-service";
import ContentAccordion from "../accordion/content-accordion";
import DynamicButton from "../dynamic-button/dynamic-button";
import DynamicCardContainer from "../dynamic-card-container/dynamic-card-container";
import TabbedContent from "../tabbed-content/tabbed-content";
import "./dynamic-component-container.scss";

export type DynamicComponentContainerProps = {
  content: DynamicContentContainerConfig;
  featureToggles: FeatureToggle[];
  iconPack: IconPack[];
};

// This component is used in the Dynamic card grid, Dynamic Tabbed components, or any other dynamic component similar to those two as a wrapper/container.

const DynamicComponentContainer: React.FC<DynamicComponentContainerProps> = ({
  content,
  iconPack,
  featureToggles
}: DynamicComponentContainerProps) => {
  const breakpoint = useBreakpoint();
  const containerCtaButtons = content?.container_cta?.cta_buttons;
  const ctaOrientation = content?.container_cta?.cta_orientation?.toLowerCase();
  const containerCta = containerCtaButtons?.length > 0 && (
    <div className={`container-cta-${ctaOrientation}`} data-testid={`cta-${ctaOrientation}`}>
      {containerCtaButtons.map((btnContent, index) => (
        <DynamicButton
          content={btnContent}
          buttonId={btnContent.settings?.button_id}
          fullWidth={breakpoint < DeviceType.Tablet}
          iconPack={iconPack}
          key={`container-cta-${btnContent.settings?.button_id}-${index}`}
        />
      ))}
    </div>
  );

  const referenceContent: any = { ...content?.content_reference?.[0] };

  const bgColor = content?.container_background_color === "white" ? "bg-white" : "bg-grey";

  const widebackContainer = content?.container_layout_size === "Wideback";

  const containerPadding = content.content_reference?.[0]?.internal?.type === "sh_tabbed_content_container" || bgColor !== "bg-white";

  const containerStroke = content.content_reference?.[0]?.internal?.type === "sh_tabbed_content_container" && bgColor === "bg-white";

  const generateSectionHeadline = () => {
    return React.createElement(
      content?.container_headline_type ? content.container_headline_type.toLowerCase() : "h3",
      { className: "xl-6 section-headline" },
      content?.container_headline
    );
  };

  return (
    <Box
      className={`dynamic-component-container ${bgColor} ${containerPadding && "container-padding"} ${
        containerStroke && "container-stroke"
      } ${content.content_reference?.[0]?.internal?.type === "sh_tabbed_content_container" && "tabbed-content-overflow"}`}
      mx={"auto"}
      width={widebackContainer ? 1 : layout.pageWidth}
      px={widebackContainer ? 0 : layout.dynamicContainerMarginX}
      data-testid="dynamic-component-container"
      id={content?.container_anchor}
    >
      <Box
        mx={"auto"}
        width={widebackContainer ? layout.pageWidth : null}
        px={widebackContainer ? layout.pagePaddingX : null}
        data-testid={widebackContainer ? "content-container-test-wideback" : ""}
      >
        {content.container_headline || content.container_subheadline || content.container_body ? (
          <div className="description-text-wrapper">
            {generateSectionHeadline()}
            <div className="xl-3 section-sub-headline" data-testid="container-subheadline">
              {content?.container_subheadline}
            </div>
            <div
              className="large-text section-description"
              dangerouslySetInnerHTML={{ __html: sanitizeRichText(content?.container_body) }}
            ></div>
          </div>
        ) : null}
        {ctaOrientation === "above" && containerCta}
        <div className="children-container">
          {content.content_reference?.[0]?.internal?.type === "sh_dynamic_card_container" && (
            <DynamicCardContainer
              content={referenceContent as DynamicCardContainerContent}
              featureToggles={featureToggles}
              iconPack={iconPack}
              dynamicContainerOverride={true}
            />
          )}
          {content.content_reference?.[0]?.internal?.type === "sh_accordion_spectrumhealth_org" && (
            <ContentAccordion
              contentAccordion={referenceContent as AccordionBlock}
              featureToggles={featureToggles}
              iconPack={iconPack}
              dynamicContainerOverride={true}
            />
          )}
          {content.content_reference?.[0]?.internal?.type === "sh_tabbed_content_container" && (
            <TabbedContent
              content={referenceContent}
              featureToggles={featureToggles}
              iconPack={iconPack}
              containerBackground={content.container_background_color}
            />
          )}
        </div>
        {ctaOrientation === "below" && containerCta}
      </Box>
    </Box>
  );
};

export default DynamicComponentContainer;
